import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { PageHeader } from '@/components/layout/page-header';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import {
  SlideOverDrawer,
  DrawerHeader,
  DrawerBody,
} from '@/components/ui/slide-over-drawer';
import {
  DIDCard,
  DIDCardGrid,
  SelectionBar,
  ConfirmOrderModal,
} from '@/features/dids';
import {
  getAvailableStates,
  getAvailableNPAs,
  getAvailableRateCenters,
  searchInventory,
  type SearchInventoryResult,
  type OrderDIDsBulkResult,
} from '@/features/dids/api';
import type { DIDLocator } from '@/features/dids/types';
import { formatPhoneNumber } from '@/lib/utils';
import { Search, ChevronDown, ChevronLeft, ChevronRight, ArrowLeft, X, RotateCcw, List, SquareCheckBig, TriangleAlert, Download } from 'lucide-react';
import toast from 'react-hot-toast';

// Toll-free prefixes
const TOLL_FREE_PREFIXES = ['800', '833', '844', '855', '866', '877', '888'];

// Pagination settings
const PAGE_SIZE = 100;
const GUARDRAIL_THRESHOLD = 500;

// State name mapping
const STATE_NAMES: Record<string, string> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia',
  HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
  KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
  MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri',
  MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
  NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio',
  OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
  SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont',
  VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
  DC: 'Washington DC', PR: 'Puerto Rico',
  // Canadian provinces
  AB: 'Alberta', BC: 'British Columbia', MB: 'Manitoba', NB: 'New Brunswick',
  NL: 'Newfoundland', NS: 'Nova Scotia', NT: 'Northwest Territories', NU: 'Nunavut',
  ON: 'Ontario', PE: 'Prince Edward Island', QC: 'Quebec', SK: 'Saskatchewan', YT: 'Yukon',
};

export function DIDSearchPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const isEmbedMode = location.pathname.startsWith('/embed');

  // Filter state
  const [numberType, setNumberType] = useState<'local' | 'toll-free' | ''>('');
  const [selectedState, setSelectedState] = useState('');
  const [selectedNPA, setSelectedNPA] = useState('');
  const [selectedRateCenter, setSelectedRateCenter] = useState('');
  const [selectedNXX, setSelectedNXX] = useState('');

  // Data state
  const [availableStates, setAvailableStates] = useState<string[]>([]);
  const [availableNPAs, setAvailableNPAs] = useState<string[]>([]);
  const [availableRateCenters, setAvailableRateCenters] = useState<string[]>([]);
  const [availableNXXs, setAvailableNXXs] = useState<string[]>([]);

  // Search results
  const [results, setResults] = useState<DIDLocator[]>([]);
  const [totalResults, setTotalResults] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);

  // Selection state
  const [selectedDIDs, setSelectedDIDs] = useState<Set<string>>(new Set());

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [showGuardrailWarning, setShowGuardrailWarning] = useState(false);

  // Loading states
  const [isLoadingStates, setIsLoadingStates] = useState(true);
  const [isLoadingNPAs, setIsLoadingNPAs] = useState(false);
  const [isLoadingRateCenters, setIsLoadingRateCenters] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Modal state
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [showListDrawer, setShowListDrawer] = useState(false);

  // Client-side search for filtering loaded results
  // searchInput = immediate keystroke feedback, debouncedSearch = actual filter value
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Column filters for cross-faceted filtering of results
  const [columnFilters, setColumnFilters] = useState({
    npa: '',
    nxx: '',
    state: '',
    rateCenter: '',
  });

  // Messages
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Load available states on mount
  useEffect(() => {
    async function loadStates() {
      try {
        const states = await getAvailableStates();
        setAvailableStates(states);
      } catch (err) {
        console.error('Failed to load states:', err);
        setError('Failed to load available states');
      } finally {
        setIsLoadingStates(false);
      }
    }
    loadStates();
  }, []);

  // Debounce search input - filters after 300ms of no typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Load NPAs and Rate Centers when state changes
  useEffect(() => {
    if (!selectedState || numberType === 'toll-free') {
      setAvailableNPAs([]);
      setAvailableRateCenters([]);
      setAvailableNXXs([]);
      return;
    }

    async function loadNPAs() {
      setIsLoadingNPAs(true);
      try {
        const npas = await getAvailableNPAs(selectedState);
        setAvailableNPAs(npas);
      } catch (err) {
        console.error('Failed to load NPAs:', err);
      } finally {
        setIsLoadingNPAs(false);
      }
    }

    async function loadRateCenters() {
      setIsLoadingRateCenters(true);
      try {
        const rcs = await getAvailableRateCenters(selectedState);
        setAvailableRateCenters(rcs);
      } catch (err) {
        console.error('Failed to load rate centers:', err);
      } finally {
        setIsLoadingRateCenters(false);
      }
    }

    loadNPAs();
    loadRateCenters();
  }, [selectedState, numberType]);

  // Extract unique NXXs from search results when we have them
  useEffect(() => {
    if (results.length > 0 && selectedNPA) {
      const nxxSet = new Set<string>();
      results.forEach((did) => {
        // Extract NXX (digits 4-6) from phone number
        const cleaned = did.tn.replace(/\D/g, '');
        const digits = cleaned.startsWith('1') && cleaned.length === 11 ? cleaned.slice(1) : cleaned;
        if (digits.length >= 6) {
          const nxx = digits.slice(3, 6);
          nxxSet.add(nxx);
        }
      });
      setAvailableNXXs(Array.from(nxxSet).sort());
    } else {
      setAvailableNXXs([]);
    }
  }, [results, selectedNPA]);

  // Handle state change - BUG FIX: Don't reset state when other filters change
  const handleStateChange = (value: string) => {
    setSelectedState(value);
    // Only reset downstream filters when state changes
    setSelectedNPA('');
    setSelectedRateCenter('');
    setSelectedNXX('');
    // Don't clear results - let user search again
  };

  // Handle NPA change - BUG FIX: Don't clear state
  const handleNPAChange = (value: string) => {
    setSelectedNPA(value);
    // Clear NXX when NPA changes (it's derived from NPA results)
    setSelectedNXX('');
    // DON'T clear state or rate center - they're independent filters
  };

  // Handle Rate Center change - BUG FIX: Don't clear state
  const handleRateCenterChange = (value: string) => {
    setSelectedRateCenter(value);
    // DON'T clear state or NPA - they're independent filters
  };

  // Handle NXX change
  const handleNXXChange = (value: string) => {
    setSelectedNXX(value);
  };

  // Handle number type change
  const handleNumberTypeChange = (type: 'local' | 'toll-free') => {
    setNumberType(type);
    setSelectedState('');
    setSelectedNPA('');
    setSelectedRateCenter('');
    setSelectedNXX('');
    setResults([]);
    setTotalResults(0);
    setHasSearched(false);
    setSelectedDIDs(new Set());
    setCurrentPage(1);
  };

  // Reset all filters
  const handleResetFilters = useCallback(() => {
    setSelectedState('');
    setSelectedNPA('');
    setSelectedRateCenter('');
    setSelectedNXX('');
    setResults([]);
    setTotalResults(0);
    setHasSearched(false);
    setSelectedDIDs(new Set());
    setCurrentPage(1);
    setShowGuardrailWarning(false);
    setSearchInput('');
    setDebouncedSearch('');
  }, []);

  // Check if we have active filters
  const hasActiveFilters = selectedState || selectedNPA || selectedRateCenter || selectedNXX;

  // Validation
  const canSearch = (): boolean => {
    if (!numberType) return false; // Must select type first
    if (numberType === 'toll-free') {
      // Toll-free can search with or without a prefix selected
      return true;
    }
    // Local requires state + (rate center or area code)
    if (!selectedState) return false;
    return !!(selectedRateCenter || selectedNPA);
  };

  // Search handler
  const handleSearch = async () => {
    if (!canSearch()) return;

    setIsSearching(true);
    setError(null);
    setSuccessMessage(null);
    setSelectedDIDs(new Set());
    setCurrentPage(1);
    setShowGuardrailWarning(false);

    try {
      const searchParams =
        numberType === 'toll-free'
          ? { npa: selectedNPA }
          : {
              state: selectedState || undefined,
              npa: selectedNPA || undefined,
              nxx: selectedNXX || undefined,
              rate_center: selectedRateCenter || undefined,
            };

      const result: SearchInventoryResult = await searchInventory(searchParams);

      // Show processing message while React computes filter counts
      setIsSearching(false);
      setIsProcessing(true);

      // Use requestAnimationFrame to let browser paint the processing message
      // before setting results (which triggers heavy useMemo computations)
      requestAnimationFrame(() => {
        setResults(result.dids);
        setTotalResults(result.total);
        setHasSearched(true);

        // Show guardrail warning if we got a lot of results
        if (result.total >= GUARDRAIL_THRESHOLD) {
          setShowGuardrailWarning(true);
        }

        setIsProcessing(false);
      });

      return; // Exit early - success path handled in requestAnimationFrame
    } catch (err: unknown) {
      console.error('Search failed:', err);
      const errorMessage =
        err instanceof Error
          ? err.message
          : (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data
              ?.error?.message || 'Search failed. Please try again.';
      setError(errorMessage);
      setResults([]);
      setTotalResults(0);
      setIsSearching(false);
      setIsProcessing(false);
    }
  };

  // Selection handlers - useCallback ensures stable reference for React.memo'd DIDCard
  const handleToggleDID = useCallback((tn: string) => {
    setSelectedDIDs((prev) => {
      const next = new Set(prev);
      if (next.has(tn)) {
        next.delete(tn);
      } else {
        next.add(tn);
      }
      return next;
    });
  }, []);

  const handleReviewOrder = () => {
    if (selectedDIDs.size > 0) {
      setShowOrderModal(true);
    }
  };

  const handleOrderComplete = (result: OrderDIDsBulkResult) => {
    if (result.fail_count === 0) {
      setSuccessMessage(`Successfully ordered ${result.success_count} DID(s)!`);
      setResults((prev) => prev.filter((d) => !selectedDIDs.has(d.tn)));
      setSelectedDIDs(new Set());
    } else if (result.success_count > 0) {
      setSuccessMessage(
        `Ordered ${result.success_count} DID(s). ${result.fail_count} failed.`
      );
      const successTNs = new Set(result.successful.map((s) => s.tn));
      setResults((prev) => prev.filter((d) => !successTNs.has(d.tn)));
      setSelectedDIDs(new Set());
    } else {
      setError(`All orders failed. ${result.failed[0]?.error || 'Unknown error'}`);
    }
  };

  // Extract NPA and NXX from phone number for filtering (moved up for use in filteredResults)
  const extractNPANXX = useCallback((tn: string) => {
    const cleaned = tn.replace(/\D/g, '');
    const digits = cleaned.startsWith('1') && cleaned.length === 11 ? cleaned.slice(1) : cleaned;
    return {
      npa: digits.slice(0, 3),
      nxx: digits.slice(3, 6),
    };
  }, []);

  // Check if any column filters are active (moved up for use in filteredResults)
  const hasActiveColumnFilters = Object.values(columnFilters).some((v) => v !== '');

  // Client-side filtered results (apply column filters + text search)
  const filteredResults = useMemo(() => {
    let filtered = results;

    // First apply column filters
    if (hasActiveColumnFilters) {
      filtered = filtered.filter((did) => {
        const { npa, nxx } = extractNPANXX(did.tn);

        if (columnFilters.npa && npa !== columnFilters.npa) return false;
        if (columnFilters.nxx && nxx !== columnFilters.nxx) return false;
        if (columnFilters.state && did.state !== columnFilters.state) return false;
        if (columnFilters.rateCenter && did.rate_center !== columnFilters.rateCenter) return false;

        return true;
      });
    }

    // Then apply text search query (uses debounced value for performance)
    if (debouncedSearch.trim()) {
      const normalizedQuery = debouncedSearch.replace(/\D/g, '');
      const lowerQuery = debouncedSearch.toLowerCase().trim();

      filtered = filtered.filter((did) => {
        // Match by phone number (digits only)
        if (normalizedQuery && did.tn.includes(normalizedQuery)) return true;
        // Match by rate center
        if (did.rate_center?.toLowerCase().includes(lowerQuery)) return true;
        // Match by state
        if (did.state?.toLowerCase().includes(lowerQuery)) return true;
        return false;
      });
    }

    return filtered;
  }, [results, debouncedSearch, columnFilters, hasActiveColumnFilters, extractNPANXX]);

  // Pagination computed values (must be after filteredResults is defined)
  const totalPages = Math.ceil(filteredResults.length / PAGE_SIZE);
  const canGoPrevious = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  // Pagination handlers
  const handlePreviousPage = () => {
    if (canGoPrevious) {
      setCurrentPage((p) => p - 1);
    }
  };

  const handleNextPage = () => {
    if (canGoNext) {
      setCurrentPage((p) => p + 1);
    }
  };

  // Displayed results (page-based slice of filtered results)
  const displayedResults = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    const endIndex = startIndex + PAGE_SIZE;
    return filteredResults.slice(startIndex, endIndex);
  }, [filteredResults, currentPage]);

  // Get selected DIDs as DIDLocator objects
  const selectedDIDsList = useMemo(
    () => results.filter((d) => selectedDIDs.has(d.tn)),
    [results, selectedDIDs]
  );

  // Select all on current page (from currently displayed results)
  const handleSelectAllOnPage = () => {
    const newSelected = new Set(selectedDIDs);
    displayedResults.forEach((did) => newSelected.add(did.tn));
    setSelectedDIDs(newSelected);
  };

  // Clear client search
  const handleClearClientSearch = () => {
    setSearchInput('');
    setDebouncedSearch('');
    setCurrentPage(1);
  };

  // Clear selection
  const handleClearSelection = () => {
    setSelectedDIDs(new Set());
  };

  // Navigate back
  const handleBack = () => {
    const path = isEmbedMode ? '/embed/dids' : '/dids';
    navigate(path);
  };

  // Get state display name
  const getStateDisplay = (code: string) => {
    const name = STATE_NAMES[code];
    return name ? `${name} (${code})` : code;
  };

  // Compute counts from results for NXX dropdown (only available after search)
  const nxxCounts = useMemo(() => {
    if (results.length === 0 || !selectedNPA) return {};
    const counts: Record<string, number> = {};
    results.forEach((did) => {
      const cleaned = did.tn.replace(/\D/g, '');
      const digits = cleaned.startsWith('1') && cleaned.length === 11 ? cleaned.slice(1) : cleaned;
      if (digits.length >= 6) {
        const nxx = digits.slice(3, 6);
        counts[nxx] = (counts[nxx] || 0) + 1;
      }
    });
    return counts;
  }, [results, selectedNPA]);

  // Get NPAs filtered by rate center (from results if available)
  const filteredNPAs = useMemo(() => {
    // If we have search results and a rate center is selected, filter NPAs from results
    if (results.length > 0 && selectedRateCenter) {
      const npaSet = new Set<string>();
      results.forEach((did) => {
        if (did.rate_center === selectedRateCenter) {
          const cleaned = did.tn.replace(/\D/g, '');
          const digits = cleaned.startsWith('1') && cleaned.length === 11 ? cleaned.slice(1) : cleaned;
          if (digits.length >= 3) {
            npaSet.add(digits.slice(0, 3));
          }
        }
      });
      return Array.from(npaSet).sort();
    }
    // Otherwise return all available NPAs for the state
    return availableNPAs;
  }, [results, selectedRateCenter, availableNPAs]);

  // Compute NPA counts filtered by rate center
  const filteredNPACounts = useMemo(() => {
    if (results.length === 0) return {};
    const counts: Record<string, number> = {};
    results.forEach((did) => {
      // If rate center is selected, only count DIDs in that rate center
      if (selectedRateCenter && did.rate_center !== selectedRateCenter) return;

      const cleaned = did.tn.replace(/\D/g, '');
      const digits = cleaned.startsWith('1') && cleaned.length === 11 ? cleaned.slice(1) : cleaned;
      if (digits.length >= 3) {
        const npa = digits.slice(0, 3);
        counts[npa] = (counts[npa] || 0) + 1;
      }
    });
    return counts;
  }, [results, selectedRateCenter]);

  // Compute Rate Center counts from results
  const rateCenterCounts = useMemo(() => {
    if (results.length === 0) return {};
    const counts: Record<string, number> = {};
    results.forEach((did) => {
      if (did.rate_center) {
        counts[did.rate_center] = (counts[did.rate_center] || 0) + 1;
      }
    });
    return counts;
  }, [results]);

  // Get unique filter options with counts from results
  const filterOptions = useMemo(() => {
    const npaCounts = new Map<string, number>();
    const nxxCounts = new Map<string, number>();
    const nxxRateCenters = new Map<string, Map<string, number>>();
    const stateCounts = new Map<string, number>();
    const rateCenterCountsMap = new Map<string, number>();

    results.forEach((did) => {
      const { npa, nxx } = extractNPANXX(did.tn);
      npaCounts.set(npa, (npaCounts.get(npa) || 0) + 1);
      nxxCounts.set(nxx, (nxxCounts.get(nxx) || 0) + 1);

      // Track rate centers per NXX
      if (did.rate_center) {
        if (!nxxRateCenters.has(nxx)) {
          nxxRateCenters.set(nxx, new Map());
        }
        const rcMap = nxxRateCenters.get(nxx)!;
        rcMap.set(did.rate_center, (rcMap.get(did.rate_center) || 0) + 1);
      }

      if (did.state) {
        stateCounts.set(did.state, (stateCounts.get(did.state) || 0) + 1);
      }
      if (did.rate_center) {
        rateCenterCountsMap.set(did.rate_center, (rateCenterCountsMap.get(did.rate_center) || 0) + 1);
      }
    });

    // For NXX, determine the primary rate center (most common one)
    const getNxxRateCenter = (nxx: string): string => {
      const rcMap = nxxRateCenters.get(nxx);
      if (!rcMap || rcMap.size === 0) return '';
      if (rcMap.size === 1) {
        return Array.from(rcMap.keys())[0] || '';
      }
      let maxCount = 0;
      let primaryRC = '';
      rcMap.forEach((count, rc) => {
        if (count > maxCount) {
          maxCount = count;
          primaryRC = rc;
        }
      });
      return primaryRC;
    };

    return {
      npas: Array.from(npaCounts.entries())
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([value, count]) => ({ value, count })),
      nxxs: Array.from(nxxCounts.entries())
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([value, count]) => ({ value, count, rateCenter: getNxxRateCenter(value) })),
      states: Array.from(stateCounts.entries())
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([value, count]) => ({ value, count })),
      rateCenters: Array.from(rateCenterCountsMap.entries())
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([value, count]) => ({ value, count })),
    };
  }, [results, extractNPANXX]);

  // Pre-compute ALL filter counts in a single pass (O(n) instead of O(n × options))
  // Each dimension's counts reflect OTHER active filters but NOT its own (cross-filtering)
  const filterCounts = useMemo(() => {
    const npaCounts = new Map<string, number>();
    const nxxCounts = new Map<string, number>();
    const rcCounts = new Map<string, number>();

    // Single pass through results array
    results.forEach((did) => {
      const { npa, nxx } = extractNPANXX(did.tn);
      const rc = did.rate_center || '';

      // NPA counts: filter by active NXX + Rate Center (but NOT active NPA)
      const matchesNXXForNPA = !columnFilters.nxx || nxx === columnFilters.nxx;
      const matchesRCForNPA = !columnFilters.rateCenter || rc === columnFilters.rateCenter;
      if (matchesNXXForNPA && matchesRCForNPA) {
        npaCounts.set(npa, (npaCounts.get(npa) || 0) + 1);
      }

      // NXX counts: filter by active NPA + Rate Center (but NOT active NXX)
      const matchesNPAForNXX = !columnFilters.npa || npa === columnFilters.npa;
      const matchesRCForNXX = !columnFilters.rateCenter || rc === columnFilters.rateCenter;
      if (matchesNPAForNXX && matchesRCForNXX) {
        nxxCounts.set(nxx, (nxxCounts.get(nxx) || 0) + 1);
      }

      // Rate Center counts: filter by active NPA + NXX (but NOT active Rate Center)
      const matchesNPAForRC = !columnFilters.npa || npa === columnFilters.npa;
      const matchesNXXForRC = !columnFilters.nxx || nxx === columnFilters.nxx;
      if (matchesNPAForRC && matchesNXXForRC) {
        rcCounts.set(rc, (rcCounts.get(rc) || 0) + 1);
      }
    });

    return { npaCounts, nxxCounts, rcCounts };
  }, [results, columnFilters, extractNPANXX]);

  // Column filter change handler
  const handleColumnFilterChange = (field: keyof typeof columnFilters, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [field]: value }));
    setCurrentPage(1); // Reset to page 1 when filters change
  };

  // Clear all column filters
  const clearColumnFilters = () => {
    setColumnFilters({ npa: '', nxx: '', state: '', rateCenter: '' });
  };

  // Reset column filters when search results change (new search executed)
  useEffect(() => {
    setColumnFilters({ npa: '', nxx: '', state: '', rateCenter: '' });
  }, [results]);

  return (
    <div className="min-h-screen">
      <PageHeader title="Phone Numbers" />

      <div className="p-6">
        {/* Back button */}
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 mb-6 transition-colors"
        >
          <ArrowLeft size={16} />
          Back to Phone Numbers
        </button>

        {/* Search Section */}
        <div className="mb-8">
          {/* Header with step indicator */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">
                <Search size={20} className="text-slate-500" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-slate-900">
                  Search Available Numbers
                </h2>
                {!hasSearched && (
                  <p className="text-sm text-slate-500 mt-0.5">
                    {!numberType ? (
                      <span className="text-amber-600 font-medium">Select Local or Toll-Free to begin</span>
                    ) : numberType === 'local' ? (
                      !selectedState ? (
                        <span className="text-amber-600 font-medium">Select a state to continue</span>
                      ) : !selectedRateCenter && !selectedNPA ? (
                        <span className="text-amber-600 font-medium">Choose a Rate Center or Area Code</span>
                      ) : (
                        <span className="text-green-600 font-medium">Ready to search!</span>
                      )
                    ) : (
                      <span className="text-green-600 font-medium">Select a prefix (optional) and search</span>
                    )}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Filter Bar */}
          <div className="flex flex-wrap items-end gap-3">
            {/* Number Type - FIRST */}
            <div className={`relative ${!numberType && !hasSearched ? 'ring-2 ring-amber-400 ring-offset-2 rounded-lg' : ''}`}>
              <FilterDropdown
                label="TYPE"
                value={numberType}
                placeholder="Select Type"
                options={[
                  { value: 'local', label: 'Local DID' },
                  { value: 'toll-free', label: 'Toll-Free' },
                ]}
                onChange={(v) => handleNumberTypeChange(v as 'local' | 'toll-free')}
              />
            </div>

            {/* For Toll-Free: Area Code (prefix) selector */}
            {numberType === 'toll-free' && (
              <FilterDropdown
                label="PREFIX"
                value={selectedNPA}
                placeholder="Select Prefix"
                options={TOLL_FREE_PREFIXES.map((p) => ({ value: p, label: p }))}
                onChange={handleNPAChange}
              />
            )}

            {/* For Local: Step 1 - State */}
            {numberType === 'local' && (
              <div className={`relative ${!selectedState && !hasSearched ? 'ring-2 ring-amber-400 ring-offset-2 rounded-lg' : ''}`}>
                <FilterDropdown
                  label="STATE"
                  value={selectedState}
                  placeholder="Select State"
                  options={availableStates.map((s) => ({
                    value: s,
                    label: getStateDisplay(s),
                  }))}
                  onChange={handleStateChange}
                  disabled={isLoadingStates}
                  loading={isLoadingStates}
                />
              </div>
            )}

            {/* For Local: Step 2 - Rate Center */}
            {numberType === 'local' && (
              <div className={`relative ${selectedState && !selectedRateCenter && !selectedNPA && !hasSearched ? 'ring-2 ring-amber-400 ring-offset-2 rounded-lg' : ''}`}>
                <FilterDropdown
                  label="RATE CENTER"
                  value={selectedRateCenter}
                  placeholder={selectedState ? 'Select...' : 'Select state first'}
                  options={availableRateCenters.map((rc) => ({
                    value: rc,
                    label: rateCenterCounts[rc] ? `${rc} (${rateCenterCounts[rc]})` : rc,
                  }))}
                  onChange={handleRateCenterChange}
                  disabled={!selectedState || isLoadingRateCenters}
                  loading={isLoadingRateCenters}
                />
              </div>
            )}

            {/* "or" separator for Local */}
            {numberType === 'local' && selectedState && !selectedRateCenter && !selectedNPA && !hasSearched && (
              <span className="text-sm text-slate-400 font-medium self-center pb-1">or</span>
            )}

            {/* For Local: Step 2 (alt) - Area Code */}
            {numberType === 'local' && (
              <div className={`relative ${selectedState && !selectedRateCenter && !selectedNPA && !hasSearched ? 'ring-2 ring-amber-400 ring-offset-2 rounded-lg' : ''}`}>
                <FilterDropdown
                  label="AREA CODE"
                  value={selectedNPA}
                  placeholder={selectedState ? 'Select...' : 'Select state first'}
                  options={filteredNPAs.map((n) => ({
                    value: n,
                    label: filteredNPACounts[n] ? `${n} (${filteredNPACounts[n]})` : n,
                  }))}
                  onChange={handleNPAChange}
                  disabled={!selectedState || isLoadingNPAs}
                  loading={isLoadingNPAs}
                />
              </div>
            )}

            {/* Exchange (only show if area code selected and we have results) - Local only */}
            {numberType === 'local' && selectedNPA && availableNXXs.length > 0 && (
              <FilterDropdown
                label="EXCHANGE"
                value={selectedNXX}
                placeholder="All"
                options={availableNXXs.map((nxx) => ({
                  value: nxx,
                  label: nxxCounts[nxx] ? `${nxx} (${nxxCounts[nxx]})` : nxx,
                }))}
                onChange={handleNXXChange}
              />
            )}

            {/* Reset Filters Button */}
            {hasActiveFilters && (
              <button
                onClick={handleResetFilters}
                className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors h-[42px] px-3"
              >
                <RotateCcw size={14} />
                Reset
              </button>
            )}

            {/* Execute Search Button */}
            <Button
              onClick={handleSearch}
              disabled={!canSearch() || isSearching}
              className="bg-slate-900 hover:bg-slate-800 text-white px-6 h-[42px]"
            >
              <Search size={16} className="mr-2" />
              Execute Search
            </Button>
          </div>

          {/* Filter Chips */}
          {hasActiveFilters && (
            <div className="flex flex-wrap items-center gap-2 mt-4">
              {selectedState && (
                <FilterChip
                  label={`State: ${getStateDisplay(selectedState)}`}
                  onRemove={() => handleStateChange('')}
                />
              )}
              {selectedRateCenter && (
                <FilterChip
                  label={`Rate Center: ${selectedRateCenter}`}
                  onRemove={() => handleRateCenterChange('')}
                />
              )}
              {selectedNPA && (
                <FilterChip
                  label={`Area Code: ${selectedNPA}`}
                  onRemove={() => handleNPAChange('')}
                />
              )}
              {selectedNXX && (
                <FilterChip
                  label={`Exchange: ${selectedNXX}`}
                  onRemove={() => handleNXXChange('')}
                />
              )}
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
            {error}
          </div>
        )}

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl text-green-700 flex items-center justify-between">
            <span>{successMessage}</span>
            <Button variant="secondary" size="sm" onClick={handleBack}>
              Return to DIDs
            </Button>
          </div>
        )}

        {/* Results Section */}
        {isSearching ? (
          <div className="flex items-center justify-center py-20">
            <Spinner size="lg" />
          </div>
        ) : isProcessing ? (
          <div className="flex items-center justify-center py-20 text-slate-500">
            <Spinner size="lg" className="mr-3" />
            <span>Gathering available inventory and building filters...</span>
          </div>
        ) : hasSearched ? (
          results.length > 0 ? (
            <>
              {/* Guardrail Warning Banner */}
              {showGuardrailWarning && (
                <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
                  <TriangleAlert size={20} className="text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">
                      Large result set ({totalResults.toLocaleString()}+ numbers)
                    </p>
                    <p className="text-sm text-amber-700 mt-0.5">
                      Consider narrowing your search with additional filters (Rate Center, Area Code, or Exchange)
                      to find specific numbers faster.
                    </p>
                  </div>
                  <button
                    onClick={() => setShowGuardrailWarning(false)}
                    className="ml-auto p-1 rounded hover:bg-amber-100"
                  >
                    <X size={16} className="text-amber-500" />
                  </button>
                </div>
              )}

              {/* Column Filter Bar - Cross-faceted filtering */}
              <div className="mb-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-slate-700">Filter Results</span>
                  {hasActiveColumnFilters && (
                    <button
                      onClick={clearColumnFilters}
                      className="text-xs text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
                    >
                      <X size={12} />
                      Clear Filters
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {/* NPA (Area Code) Column Filter */}
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Area Code (NPA)</label>
                    <select
                      value={columnFilters.npa}
                      onChange={(e) => handleColumnFilterChange('npa', e.target.value)}
                      className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    >
                      <option value="">All ({results.length})</option>
                      {filterOptions.npas.map(({ value }) => {
                        const count = filterCounts.npaCounts.get(value) || 0;
                        return (
                          <option key={value} value={value} disabled={count === 0}>
                            {value} ({count})
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  {/* NXX (Exchange) Column Filter */}
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Exchange (NXX)</label>
                    <select
                      value={columnFilters.nxx}
                      onChange={(e) => handleColumnFilterChange('nxx', e.target.value)}
                      className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    >
                      <option value="">All ({filteredResults.length})</option>
                      {filterOptions.nxxs.map(({ value, rateCenter }) => {
                        const count = filterCounts.nxxCounts.get(value) || 0;
                        const displayText = rateCenter
                          ? `${value} (${count}) - ${rateCenter}`
                          : `${value} (${count})`;
                        return (
                          <option key={value} value={value} disabled={count === 0}>
                            {displayText}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  {/* Rate Center Column Filter */}
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Rate Center</label>
                    <select
                      value={columnFilters.rateCenter}
                      onChange={(e) => handleColumnFilterChange('rateCenter', e.target.value)}
                      className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    >
                      <option value="">All ({filteredResults.length})</option>
                      {filterOptions.rateCenters.map(({ value }) => {
                        const count = filterCounts.rcCounts.get(value) || 0;
                        return (
                          <option key={value} value={value} disabled={count === 0}>
                            {value} ({count})
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>
              </div>

              {/* No Results Message (when filters produce no matches) */}
              {filteredResults.length === 0 && hasActiveColumnFilters && (
                <div className="mb-4 py-8 text-center bg-amber-50 border border-amber-200 rounded-xl">
                  <p className="text-amber-700 font-medium">No matching numbers</p>
                  <p className="text-amber-600 text-sm mt-1">
                    Try adjusting your filters or{' '}
                    <button
                      onClick={clearColumnFilters}
                      className="text-indigo-600 hover:text-indigo-700 underline"
                    >
                      clear all filters
                    </button>
                  </p>
                </div>
              )}

              {/* Results Header - Row 1: Counts + Search + View as List */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                  <p className="text-sm text-slate-500">
                    Showing{' '}
                    <span className="font-medium text-slate-700">
                      {Math.min((currentPage - 1) * PAGE_SIZE + 1, filteredResults.length)}
                      –{Math.min(currentPage * PAGE_SIZE, filteredResults.length)}
                    </span>
                    {' '}of{' '}
                    <span className="font-medium text-slate-700">{filteredResults.length.toLocaleString()}</span>
                    {' '}available DIDs
                    {filteredResults.length !== results.length && (
                      <span className="text-slate-400"> (filtered from {results.length.toLocaleString()})</span>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {/* Client-Side Search (debounced) */}
                  <div className="relative">
                    <Search
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                    />
                    <input
                      type="text"
                      placeholder="Filter these results..."
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      className="w-[220px] pl-9 pr-8 py-1.5 text-sm bg-slate-50 border border-slate-200 rounded-lg placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                    {searchInput && (
                      <button
                        onClick={handleClearClientSearch}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-200"
                      >
                        <X size={14} className="text-slate-400" />
                      </button>
                    )}
                  </div>

                  {/* View as List Button - Prominent */}
                  <Button
                    variant="secondary"
                    onClick={() => setShowListDrawer(true)}
                    className="flex items-center gap-1.5"
                  >
                    <List size={16} />
                    View as List
                  </Button>
                </div>
              </div>

              {/* Results Header - Row 2: Selection Actions */}
              <div className="flex items-center gap-3 mb-4">
                {/* Select All Visible */}
                <button
                  onClick={handleSelectAllOnPage}
                  className="flex items-center gap-1.5 text-sm text-indigo-600 hover:text-indigo-700 transition-colors"
                >
                  <SquareCheckBig size={14} />
                  Select all on page
                </button>
                {selectedDIDs.size > 0 && (
                  <>
                    <span className="text-slate-300">·</span>
                    <button
                      onClick={handleClearSelection}
                      className="text-sm text-slate-500 hover:text-slate-700 transition-colors"
                    >
                      Clear selection
                    </button>
                    <span className="text-slate-300">·</span>
                    <p className="text-sm text-indigo-600 font-medium">
                      {selectedDIDs.size} selected
                    </p>
                  </>
                )}
              </div>

              {/* DID Card Grid */}
              <DIDCardGrid>
                {displayedResults.map((did) => (
                  <DIDCard
                    key={did.tn}
                    did={did}
                    isSelected={selectedDIDs.has(did.tn)}
                    onSelect={handleToggleDID}
                  />
                ))}
              </DIDCardGrid>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="mt-6 flex items-center justify-between px-2">
                  <span className="text-sm text-slate-500">
                    Page {currentPage} of {totalPages}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={!canGoPrevious}
                      onClick={handlePreviousPage}
                      className="flex items-center gap-1"
                    >
                      <ChevronLeft size={16} />
                      Previous
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={!canGoNext}
                      onClick={handleNextPage}
                      className="flex items-center gap-1"
                    >
                      Next
                      <ChevronRight size={16} />
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search size={24} className="text-slate-400" />
              </div>
              <h3 className="text-lg font-medium text-slate-900 mb-2">No Results Found</h3>
              <p className="text-slate-500">
                Try adjusting your search criteria to find available numbers.
              </p>
            </div>
          )
        ) : (
          <div className="text-center py-20">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search size={24} className="text-slate-400" />
            </div>
            <h3 className="text-lg font-medium text-slate-900 mb-2">Ready to Search</h3>
            <p className="text-slate-500">
              {!numberType
                ? 'Select Local DID or Toll-Free to begin.'
                : numberType === 'toll-free'
                  ? 'Select a toll-free prefix (optional) and click Execute Search.'
                  : 'Select a state and area code or rate center, then click Execute Search.'}
            </p>
          </div>
        )}
      </div>

      {/* Selection Bar */}
      <SelectionBar count={selectedDIDs.size} onReviewOrder={handleReviewOrder} />

      {/* Confirmation Modal */}
      <ConfirmOrderModal
        isOpen={showOrderModal}
        onClose={() => setShowOrderModal(false)}
        selectedDIDs={selectedDIDsList}
        onOrderComplete={handleOrderComplete}
      />

      {/* List View Drawer - Shows ALL filtered results, not just visible slice */}
      <DIDListDrawer
        isOpen={showListDrawer}
        onClose={() => setShowListDrawer(false)}
        dids={filteredResults}
        selectedDIDs={selectedDIDs}
        onToggleSelect={handleToggleDID}
        onSelectAll={() => {
          // Select all filtered results
          const newSelected = new Set(selectedDIDs);
          filteredResults.forEach((did) => newSelected.add(did.tn));
          setSelectedDIDs(newSelected);
        }}
        onClearSelection={handleClearSelection}
      />
    </div>
  );
}

// Filter dropdown component
interface FilterDropdownProps {
  label: string;
  value: string;
  placeholder?: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
  disabled?: boolean;
  loading?: boolean;
}

function FilterDropdown({
  label,
  value,
  placeholder = 'Select...',
  options,
  onChange,
  disabled,
  loading,
}: FilterDropdownProps) {
  return (
    <div className="relative">
      <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">
        {label}
      </label>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled || loading}
          className="appearance-none bg-white border border-slate-200 rounded-lg px-4 py-2.5 pr-10 text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-slate-50 disabled:text-slate-400 min-w-[140px]"
        >
          <option value="">{loading ? 'Loading...' : placeholder}</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <ChevronDown
          size={16}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
        />
      </div>
    </div>
  );
}

// Filter chip component
interface FilterChipProps {
  label: string;
  onRemove: () => void;
}

function FilterChip({ label, onRemove }: FilterChipProps) {
  return (
    <span className="inline-flex items-center gap-1.5 bg-indigo-50 text-indigo-700 rounded-full px-3 py-1 text-sm font-medium">
      {label}
      <button
        onClick={onRemove}
        className="p-0.5 rounded-full hover:bg-indigo-100 transition-colors"
      >
        <X size={12} />
      </button>
    </span>
  );
}

// DID List Drawer component
interface DIDListDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  dids: DIDLocator[];
  selectedDIDs: Set<string>;
  onToggleSelect: (tn: string) => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
}

function DIDListDrawer({
  isOpen,
  onClose,
  dids,
  selectedDIDs,
  onToggleSelect,
  onSelectAll,
  onClearSelection,
}: DIDListDrawerProps) {
  const [searchTerm, setSearchTerm] = useState('');

  // Filter DIDs based on search
  const filteredDids = useMemo(() => {
    if (!searchTerm.trim()) return dids;
    const normalizedSearch = searchTerm.replace(/\D/g, '');
    return dids.filter((did) => did.tn.includes(normalizedSearch));
  }, [dids, searchTerm]);

  const handleExportCSV = () => {
    if (!dids || dids.length === 0) {
      toast.error('No numbers to export');
      return;
    }

    const csvContent = [
      'Phone Number,Rate Center,State,SMS,T38,Tier',
      ...dids.map(
        (did) =>
          `${formatPhoneNumber(did.tn)},${did.rate_center || ''},${did.state || ''},${did.sms ? 'Yes' : 'No'},${did.t38 ? 'Yes' : 'No'},${did.tier || ''}`
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `available-dids-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success(`Exported ${dids.length} numbers to CSV`);
  };

  const handleClose = () => {
    setSearchTerm('');
    onClose();
  };

  return (
    <SlideOverDrawer isOpen={isOpen} onClose={handleClose} width="md">
      <DrawerHeader title={`Available Numbers (${dids.length})`} onClose={handleClose} />

      <DrawerBody className="flex flex-col">
        {/* Search and Actions */}
        <div className="p-4 border-b border-slate-200 flex items-center gap-3">
          <div className="relative flex-1">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              type="text"
              placeholder="Search numbers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
          <Button variant="secondary" onClick={handleExportCSV} className="flex-shrink-0">
            <Download size={16} className="mr-2" />
            Export
          </Button>
        </div>

        {/* Selection Actions */}
        <div className="px-4 py-2 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
          <span className="text-xs text-slate-500">
            {selectedDIDs.size > 0
              ? `${selectedDIDs.size} selected`
              : `${filteredDids.length} numbers`}
          </span>
          <div className="flex items-center gap-3">
            <button
              onClick={onSelectAll}
              className="text-xs text-indigo-600 hover:text-indigo-700"
            >
              Select all
            </button>
            {selectedDIDs.size > 0 && (
              <button
                onClick={onClearSelection}
                className="text-xs text-slate-500 hover:text-slate-700"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Number List */}
        <div className="flex-1 overflow-y-auto">
          {filteredDids.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              {searchTerm.trim() ? 'No numbers match your search' : 'No numbers available'}
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filteredDids.map((did, index) => (
                <div
                  key={did.tn}
                  onClick={() => onToggleSelect(did.tn)}
                  className={`px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-slate-50 animate-fade-in ${
                    selectedDIDs.has(did.tn) ? 'bg-indigo-50' : ''
                  }`}
                  style={{ animationDelay: `${Math.min(index * 10, 500)}ms` }}
                >
                  <div>
                    <span className="font-mono text-sm text-indigo-600 font-medium">
                      {formatPhoneNumber(did.tn)}
                    </span>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {did.rate_center} · {did.state}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {did.sms && (
                      <span className="text-[10px] font-medium text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded">
                        SMS
                      </span>
                    )}
                    {did.t38 && (
                      <span className="text-[10px] font-medium text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded">
                        FAX
                      </span>
                    )}
                    <div
                      className={`w-5 h-5 rounded-full flex items-center justify-center ${
                        selectedDIDs.has(did.tn)
                          ? 'bg-indigo-500 text-white'
                          : 'bg-slate-100 text-slate-300'
                      }`}
                    >
                      <SquareCheckBig size={12} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DrawerBody>
    </SlideOverDrawer>
  );
}
